import { FC, useState, useEffect } from 'react';
import Modal from '../modal/Modal';
import Loader from '../loader/Loader';
import { InfoSVG } from '../svgs';
import { QBAccountAssociation, QBCustomerInformation } from '../../types/interfaces/quickbooks.interface';
import { showToast } from '../../services/toast.service';
import { getQbAssociationList, updateQbCustomerReferenceNumber } from '../../api/quickbooksAccountAssociationApi';
import { getQuickBooksCustomerByRefNumber } from '../../api/quickbooksApi';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, INVENTORY_CONSTANTS, LABEL_CONSTANTS } from '../../constants/common';
import './qbEditModal.scss';

interface InfoRowProps {
  label: string;
  value: string | undefined;
}

const InfoRow: FC<InfoRowProps> = ({ label, value }) => {
  return (
    <div className="row">
      <div className="l-column">{label}: </div>
      <div className="r-column">
        <b>{value ?? '--'}</b>
      </div>
    </div>
  );
};

export type QbEditModalProps = {
  isOpen: boolean;
  width?: number;
  setIsOpen: (e: boolean) => void;
  onConfirm?: () => void;
  customerInformation?: QBCustomerInformation;
  accountId?: number;
};

const QbEditModal = ({ isOpen, width = 700, setIsOpen, customerInformation, accountId, onConfirm }: QbEditModalProps) => {
  const [customerRefNumber, setCustomerRefNumber] = useState<string>('');
  const [initialCustomerRefNumber, setInitialCustomerRefNumber] = useState<string>('');
  const [linkedAccounts, setLinkedAccounts] = useState<QBAccountAssociation[]>([]);
  const [qbCustomer, setQbCustomer] = useState<QBCustomerInformation | null>(null);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleClose = () => {
    setCustomerRefNumber('');
    setInitialCustomerRefNumber('');
    revertStates();
    setIsOpen(false);
  };

  const revertStates = () => {
    setLinkedAccounts([]);
    setQbCustomer(null);
    setIsChecked(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    setCustomerRefNumber(value);
    revertStates();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    if (accountId) {
      try {
        await updateQbCustomerReferenceNumber(Number(accountId), customerRefNumber);
        showToast.success(toastMessages.QB_CUSTOMER_REF_NUMBER_UPDATED);
        setCustomerRefNumber('');
        setInitialCustomerRefNumber('');
        revertStates();
        onConfirm?.();
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsOpen(false);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && customerInformation) {
      const refNumber = customerInformation?.customerRefId || '';
      setCustomerRefNumber(refNumber);
      setInitialCustomerRefNumber(refNumber);
    }
  }, [isOpen, customerInformation]);

  const getQbAssociation = async () => {
    setLoading(true);
    if (accountId) {
      try {
        const { data: qbAssociationList } = await getQbAssociationList({
          include: 'Account',
          filter: `quickBooksCustomerRef eq ${customerRefNumber}`,
        });
        setLinkedAccounts(qbAssociationList);

        const { data: qbCustomerData } = await getQuickBooksCustomerByRefNumber(customerRefNumber);
        setQbCustomer(qbCustomerData);
      } catch (error: any) {
        if (error.response?.status === 500 || error.response?.status === 404) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      } finally {
        setIsChecked(true);
      }
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width={width} ignoreBackdrop>
      <div className="qb-edit-modal">
        <div className="header">
          <h4>{LABEL_CONSTANTS.EDIT_CUSTOMER_REF_NUMBER}</h4>
        </div>
        <div className="body">
          <div className="body-content">
            {customerInformation && (
              <>
                <InfoRow label={LABEL_CONSTANTS.QB_CUSTOMER_NAME} value={customerInformation?.displayName} />
                <InfoRow label={LABEL_CONSTANTS.QB_EMAIL_ADDRESS} value={customerInformation?.primaryEmail} />
                <InfoRow label={LABEL_CONSTANTS.QB_PHONE_NUMBER} value={customerInformation?.primaryPhone} />
              </>
            )}
            {loading && <Loader loaderSize={'medium'} />}
            <div className="row">
              <div className="l-column">{`*${LABEL_CONSTANTS.QB_REFERENCE_NUMBER}:`}</div>
              <div className="r-column">
                <input
                  value={customerRefNumber}
                  type="number"
                  placeholder="Customer Reference Number"
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>
          {isChecked && linkedAccounts.length > 0 && (
            <div className="linked-accounts">
              <h5>{INVENTORY_CONSTANTS.ACCOUNTS_WITH_REF_NUMBER}</h5>
              <table className="linked-accounts-table">
                <thead>
                  <tr>
                    <th>{LABEL_CONSTANTS.ACCOUNT_NAME}</th>
                    <th>{LABEL_CONSTANTS.ACCOUNT_ID}</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedAccounts.map((linkedAccount, index) => (
                    <tr key={index}>
                      <td>{linkedAccount.account?.name}</td>
                      <td>{linkedAccount.accountId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {isChecked && linkedAccounts.length === 0 && (
            <div className="linked-accounts">
              <h5>{INVENTORY_CONSTANTS.NO_ACCOUNTS_WITH_REF_NUMBER}</h5>
            </div>
          )}
          {isChecked && qbCustomer && (
            <div className="qb-customer-details">
              <h5>
                <InfoSVG size="28" />{' '}
                <span className="text-wrapper">
                  {INVENTORY_CONSTANTS.CUSTOMER_WITH_REF_NUMBER_PREFIX} <strong>{qbCustomer?.displayName}</strong>{' '}
                  {INVENTORY_CONSTANTS.CUSTOMER_WITH_REF_NUMBER_SUFFIX}
                </span>
              </h5>
            </div>
          )}
          {isChecked && !qbCustomer && (
            <div className="no-qb-customer-details">
              <h5>
                <InfoSVG size="20" /> {INVENTORY_CONSTANTS.NO_CUSTOMER_WITH_REF_NUMBER}
              </h5>
            </div>
          )}
        </div>
        <div className="footer">
          <button className="button blue inverted small" onClick={handleClose} disabled={loading}>
            {BUTTON_CONSTANTS.CANCEL}
          </button>
          {!isChecked ? (
            <button
              className="button green small"
              onClick={getQbAssociation}
              disabled={loading || !customerRefNumber?.trim() || customerRefNumber === initialCustomerRefNumber}>
              {BUTTON_CONSTANTS.CHECK}
            </button>
          ) : (
            <button
              className="button green small"
              onClick={handleSubmit}
              disabled={!customerRefNumber?.trim() || customerRefNumber === initialCustomerRefNumber || loading || !qbCustomer}>
              {BUTTON_CONSTANTS.UPDATE}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default QbEditModal;
